package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import com.bitterinfant.shared.SiteLinks
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Cross-links the three properties. Pass the current site so it can be marked active/omitted. */
enum class Site(val label: String, val url: String) {
    STUDIO("Studio", SiteLinks.STUDIO),
    LAURA("Laura", SiteLinks.LAURA),
    MATTHEW("Matthew", SiteLinks.MATTHEW),
}

@Composable
fun SiteHeader(current: Site, title: String, tagline: String, titleFontFamily: String? = null) {
    Header(attrs = {
        style {
            property("background", Theme.bgElevated)
            property("border-bottom", "1px solid ${Theme.border}")
            padding(1.5.cssRem, 1.5.cssRem)
        }
    }) {
        Div(attrs = {
            style {
                maxWidth(Theme.maxWidth); property("margin", "0 auto")
                display(DisplayStyle.Flex)
                property("justify-content", "space-between")
                property("align-items", "center")
                flexWrap(FlexWrap.Wrap)
                property("gap", "1rem")
            }
        }) {
            Div {
                H1(attrs = {
                    style {
                        margin(0.px)
                        fontSize(1.6.cssRem)
                        color(Color(Theme.text))
                        titleFontFamily?.let { property("font-family", it) }
                    }
                }) { Text(title) }
                P(attrs = { style { margin(0.px); color(Color(Theme.textMuted)) } }) { Text(tagline) }
            }
            // Only the studio site (the hub) links out to the two talent sites.
            // Matthew's and Laura's pages carry no outbound nav, by design.
            if (current == Site.STUDIO) {
                Nav {
                    Site.entries.forEach { site ->
                        if (site != current) {
                            A(href = site.url, attrs = {
                                style {
                                    color(Color(Theme.accent))
                                    marginLeft(1.2.cssRem)
                                    textDecoration("none")
                                }
                            }) { Text(site.label) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SiteFooter(current: Site) {
    Footer(attrs = {
        style {
            property("border-top", "1px solid ${Theme.border}")
            padding(1.5.cssRem, 1.5.cssRem)
            marginTop(3.cssRem)
        }
    }) {
        Div(attrs = {
            style {
                maxWidth(Theme.maxWidth); property("margin", "0 auto")
                color(Color(Theme.textMuted))
                fontSize(0.85.cssRem)
            }
        }) {
            Text("© Bitter Infant Productions.")
            // Only the studio site links out; Matthew's and Laura's pages stay dead-ends.
            if (current == Site.STUDIO) {
                Text(" ")
                A(href = SiteLinks.STUDIO, attrs = { style { color(Color(Theme.textMuted)) } }) { Text("bitterinfantproductions.com") }
            }
        }
    }
}

/**
 * Pass `showHeader = false` when the page renders its own title block (e.g. inside an
 * [ImageBanner]) — the header bar is dropped and Main loses its top padding so the page's
 * first element sits flush against the top of the viewport, with no dividing line.
 */
@Composable
fun PageShell(
    current: Site,
    title: String,
    tagline: String,
    titleFontFamily: String? = null,
    showHeader: Boolean = true,
    content: @Composable () -> Unit,
) {
    if (showHeader) SiteHeader(current, title, tagline, titleFontFamily)
    Main(attrs = {
        style {
            maxWidth(Theme.maxWidth); property("margin", "0 auto")
            padding(if (showHeader) 2.cssRem else 0.px, 1.5.cssRem, 2.cssRem)
        }
    }) { content() }
    SiteFooter(current)
}

